import { useMemo } from 'react';
import { Card, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { DailyPoint, MetricKey } from '@entities/analytics';

const { Title } = Typography;

const LINES: { key: MetricKey; tKey: string; color: string }[] = [
  { key: 'PROFILE_VIEW', tKey: 'dashboard.chart.profileViews', color: '#3b82f6' },
  { key: 'PRODUCT_VIEW', tKey: 'dashboard.chart.productViews', color: '#8b5cf6' },
  { key: 'REQUEST_RECEIVED', tKey: 'dashboard.chart.requests', color: '#f59e0b' },
  { key: 'CHAT_STARTED', tKey: 'dashboard.chart.chats', color: '#10b981' },
  { key: 'DEAL_WON', tKey: 'dashboard.chart.deals', color: '#ef4444' },
];

export function DailyChart({ data }: { data: DailyPoint[] }) {
  const { t } = useTranslation();
  const chartData = useMemo(
    () => data.map((p) => ({ date: p.day, ...p.counts })),
    [data],
  );

  return (
    <Card
      title={<Title level={5} style={{ margin: 0 }}>{t('dashboard.chart.title')}</Title>}
      bordered
      style={{ borderRadius: 12 }}
      bodyStyle={{ padding: 16, height: 320 }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} />
          <XAxis
            dataKey="date"
            tickFormatter={(d: string) =>
              new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
            }
            fontSize={11}
            stroke="var(--ant-color-text-secondary)"
          />
          <YAxis allowDecimals={false} fontSize={11} width={32} stroke="var(--ant-color-text-secondary)" />
          <Tooltip
            labelFormatter={(d: string) => new Date(d).toLocaleDateString()}
            contentStyle={{
              background: 'var(--ant-color-bg-elevated)',
              border: '1px solid var(--ant-color-border)',
              borderRadius: 8,
              fontSize: 12,
            }}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {LINES.map((line) => (
            <Line
              key={line.key}
              type="monotone"
              dataKey={line.key}
              stroke={line.color}
              name={t(line.tKey)}
              dot={false}
              strokeWidth={2}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
}
